import dagre from 'dagre';
import { Node, Edge } from 'reactflow';
import { NodeData } from '../types';

/**
 * Apply dagre hierarchical layout to nodes and edges
 */
export function applyLayout(
  nodes: Node<NodeData>[],
  edges: Edge[],
  direction: 'TB' | 'LR' = 'TB'
): Node<NodeData>[] {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));

  // Configure graph
  dagreGraph.setGraph({
    rankdir: direction, // TB = top-to-bottom, LR = left-to-right
    nodesep: 100, // Horizontal separation between nodes
    ranksep: 150, // Vertical separation between ranks
    marginx: 50,
    marginy: 50,
  });

  // Add nodes to dagre (exclude group nodes - they auto-size)
  nodes.forEach(node => {
    // Skip group nodes - ReactFlow handles their positioning based on children
    if (node.type === 'group') {
      return;
    }

    // Calculate approximate node dimensions based on content
    const width = 250;
    const height = 150;

    dagreGraph.setNode(node.id, { width, height });
  });

  // Add edges to dagre (only parent-child edges for layout, not dependencies)
  edges.forEach(edge => {
    // Only use hierarchical edges for layout (not dependency edges)
    if (!edge.id.startsWith('d-')) {
      dagreGraph.setEdge(edge.source, edge.target);
    }
  });

  // Calculate layout
  dagre.layout(dagreGraph);

  // Apply calculated positions to nodes
  const layoutedNodes = nodes.map(node => {
    // Group nodes don't get positioned by dagre - ReactFlow auto-positions them
    if (node.type === 'group') {
      return node;
    }

    const nodeWithPosition = dagreGraph.node(node.id);

    // For nodes with a parent (inside a group), use relative positioning
    const position = {
      x: nodeWithPosition.x - nodeWithPosition.width / 2,
      y: nodeWithPosition.y - nodeWithPosition.height / 2,
    };

    return {
      ...node,
      position,
    };
  });

  return layoutedNodes;
}
